// this code is based on the blog post
// https://kentcdodds.com/blog/how-to-use-react-context-effectively

import { useApolloClient } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
  debugLog,
  error as commonError,
  csvConversion,
} from '@finley-technologies/common';
import { FinleyBuffer, Util } from '@finley-technologies/finley-types';
import {
  createTapeBuilderFromArtifactsDocument,
  TapeBuilderUI,
  ITapeBuilder,
  tapeBuilderGraphToArtifactsDocument,
  AssetExtractor,
} from '@finley-technologies/tape-builder';
import {
  PDFExtractor,
  TapeDefinition,
  TransformSupplementalTapeOutput,
} from '@finley-technologies/tape-sanitizer';

import { getAllOperationNodes, useGraph } from '../GraphContext';
import { convertErrorToStringMessages } from './convertErrorToStringMessages';
import { getPdfExtractor } from './getPdfExtractor';
import { getAi2CsvExtractor } from 'packages/tapeBuilder/ui/calcAgentQA/getAi2CsvExtractor';
import { getAssetExtractor } from 'packages/tapeBuilder/ui/calcAgentQA/getAssetExtractor';
import { getPyCsvVirtualSheetExtractor } from 'packages/tapeBuilder/ui/calcAgentQA/getPyCsvVirtualSheetExtractor';
import { getPyXlsx2Csv2Extractor } from 'packages/tapeBuilder/ui/calcAgentQA/getPyXlsx2Csv2Extractor';
import { getPyXlsxVirtualSheet2CsvExtractor } from 'packages/tapeBuilder/ui/calcAgentQA/getPyXlsxVirtualSheet2CsvExtractor';
import { StructuredErrorsModal } from 'packages/tapeBuilder/ui/calcAgentQA/StructuredErrorModal';

export type TapeBuilderBuffer = FinleyBuffer | null;
export type QAState = {
  buffers: Map<string, TapeBuilderBuffer>; // map of tapeBuilderNodeId to buffer
  extensions: Map<string, QAFileExtension>;
  supplementalMap: Map<string, TransformSupplementalTapeOutput[]>;
  qaMode: boolean;
  qaStatus: string;
  errors: commonError.StructuredError[];
};

type ActionSetBuffer = {
  type: 'setBuffer';
  nodeId: string;
  buffer: TapeBuilderBuffer;
  extension: QAFileExtension;
};

type ActionSetSupplementals = {
  type: 'setSupplementals';
  nodeId: string;
  supplementals: TransformSupplementalTapeOutput[];
};

type ActionSetQAmode = {
  type: 'setQAmode';
  qaMode: boolean;
};

type ActionSetQAStatus = {
  type: 'setQAStatus';
  status: string;
};

type ActionSetQAErrors = {
  type: 'setQAErrors';
  status: commonError.StructuredError[];
};

type Action =
  | ActionSetBuffer
  | ActionSetSupplementals
  | ActionSetQAmode
  | ActionSetQAErrors
  | ActionSetQAStatus;

export const isQAFileExtension = (value: string): value is QAFileExtension => {
  return Util.SupportedFileExtensionIterable.includes(value as QAFileExtension);
};
export type QAFileExtension = Util.SupportedFileExtension;
// this context is shared between components
// TapeBuilderControl is the context provider
// and other components are consumers
export type QAContextType = {
  state: QAState;
  hasBuffer: (nodeId: string) => boolean;
  hasAllInputBuffers: (nodeId: string) => boolean;

  getNodeBuffer: (nodeId: string) => TapeBuilderBuffer;
  setNodeBuffer: (
    nodeId: string,
    buffer: TapeBuilderBuffer,
    extension: QAFileExtension,
  ) => void;
  getNodeBufferExtension: (nodeId: string) => QAFileExtension;

  getNodeSupplementals: (nodeId: string) => TransformSupplementalTapeOutput[];

  generateTape: (
    nodeId: string,
  ) => Promise<
    | {
        isValid: true;
        buffer: FinleyBuffer;
      }
    | {
        isValid: false;
      }
  >;

  generateTapeDefinition: (
    nodeId: string,
  ) =>
    | {
        isValid: true;
        tapeDefinition: TapeDefinition | null;
        tapeBuilder: ITapeBuilder;
        targetNode: TapeBuilderUI.TapeBuilderNodeAbstract;
      }
    | {
        isValid: false;
      };

  generateAllTapes: () => Promise<boolean>;
};

const QAContext = createContext<QAContextType | null>(null);
const reducer = (state: QAState, action: Action): QAState => {
  switch (action.type) {
    case 'setBuffer': {
      const buffers = new Map(state.buffers);
      buffers.set(action.nodeId, action.buffer);
      const extensions = new Map(state.extensions);
      extensions.set(action.nodeId, action.extension);

      return {
        ...state,
        buffers,
        extensions,
      };
    }
    case 'setSupplementals': {
      const supplementalMap = new Map(state.supplementalMap);
      supplementalMap.set(action.nodeId, action.supplementals);
      return { ...state, supplementalMap };
    }
    case 'setQAmode': {
      // changing qa mode will clear all buffers and errors.
      return {
        ...state,
        qaMode: action.qaMode,
        buffers: new Map(),
        errors: [],
      };
    }
    case 'setQAStatus': {
      return {
        ...state,
        qaStatus: action.status,
      };
    }

    case 'setQAErrors': {
      return {
        ...state,
        errors: action.status,
      };
    }

    default:
      return finleyAssertUnreachable(action);
  }
};

export const useQAContext = () => {
  const context = React.useContext(QAContext);

  finleyAssert(context, 'useQAContext must be used within a QAProvider');
  return context;
};

export const QAProvider = ({
  qaMode,
  pipelineDefinitionId,
  children,
}: {
  qaMode: boolean;
  pipelineDefinitionId: string;

  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, {
    buffers: new Map(),
    extensions: new Map(),
    supplementalMap: new Map<string, TransformSupplementalTapeOutput[]>(),
    qaMode,
    qaStatus: '',
    errors: [],
  });

  const latestState = useRef(state);
  const graphContext = useGraph();
  const apolloClient = useApolloClient();

  // if we need to access the latest state
  useEffect(() => {
    latestState.current = state;
  }, [state]);

  useEffect(() => {
    dispatch({
      type: 'setQAmode',
      qaMode,
    });
  }, [qaMode]);

  const hasBuffer = useCallback(
    (nodeId: string): boolean => {
      return state.buffers.has(nodeId);
    },
    [state.buffers],
  );

  const getNodeBuffer = useCallback(
    (nodeId: string) => {
      const output = state.buffers.get(nodeId) || null;
      return output;
    },
    [state.buffers],
  );

  const getNodeBufferExtension = useCallback(
    (nodeId: string) => {
      return state.extensions.get(nodeId) || 'csv';
    },
    [state.extensions],
  );

  const setNodeBuffer = (
    nodeId: string,
    buffer: TapeBuilderBuffer,
    extension: QAFileExtension,
  ) => {
    dispatch({
      type: 'setBuffer',
      nodeId,
      buffer,
      extension,
    });
  };

  const getNodeSupplementals = useCallback(
    (nodeId: string): TransformSupplementalTapeOutput[] => {
      const output = state.supplementalMap.get(nodeId) || [];
      return output;
    },
    [state.supplementalMap],
  );

  const setNodeSupplementals = (
    nodeId: string,
    supplementals: TransformSupplementalTapeOutput[],
  ): void => {
    dispatch({ type: 'setSupplementals', nodeId, supplementals });
  };

  const setQAStatus = async (status: string) => {
    dispatch({
      type: 'setQAStatus',
      status,
    });
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  const setQAErrors = (errors: commonError.StructuredError[]) => {
    dispatch({
      type: 'setQAErrors',
      status: errors,
    });
  };

  const getNodeById = (nodeId: string) => {
    const node = graphContext.state.nodes.find(
      (n) => n.metadata.tapeBuilderNodeId === nodeId,
    );

    finleyAssert(node, `node not found for id ${nodeId}`);
    return node;
  };

  const getInputNodeIds = (nodeId: string): string[] => {
    return graphContext.state.edges
      .filter((e) => e.target === nodeId)
      .map((node) => node.source);
  };

  const hasAllOutputBuffers = (nodeId: string): boolean => {
    // look for dependencies for the given nodeId
    const node = getNodeById(nodeId);

    const outputNodes = graphContext.state.edges
      .filter((e) => e.source === node.metadata.tapeBuilderNodeId)
      .map((e) => e.target);

    // see if dependencies have buffers
    const hasBuffers = outputNodes.filter((outputNodeId) =>
      hasBuffer(outputNodeId),
    );
    return hasBuffers.length === outputNodes.length;
  };

  const hasAllInputBuffersInternal = useCallback(
    (nodeId: string, qaState: QAState): boolean => {
      const node = graphContext.state.nodes.find(
        (n) => n.metadata.tapeBuilderNodeId === nodeId,
      );

      if (!node) {
        return false;
      }

      const dependencies = graphContext.state.edges.filter(
        (e) => e.target === node.metadata.tapeBuilderNodeId,
      );

      // see if dependencies have buffers
      const hasBuffers = dependencies.filter((d) => {
        const dependencyNode = graphContext.state.nodes.find(
          (n) => n.metadata.tapeBuilderNodeId === d.source,
        );
        finleyAssert(
          dependencyNode,
          `dependency node not found for id ${d.source}`,
        );

        const dependencyBuffer =
          qaState.buffers.get(dependencyNode.metadata.tapeBuilderNodeId) ||
          null;
        const hasDependentBuffer = dependencyBuffer !== null;
        return hasDependentBuffer;
      });

      return hasBuffers.length > 0 && hasBuffers.length === dependencies.length;
    },
    [graphContext.state.edges, graphContext.state.nodes],
  );

  const hasAllInputBuffers = useCallback(
    (nodeId: string): boolean => {
      // look for dependencies for the given nodeId
      return hasAllInputBuffersInternal(nodeId, state);
    },
    [hasAllInputBuffersInternal, state],
  );

  const generateTapeDefinition = (
    nodeId: string,
  ):
    | {
        isValid: true;
        tapeDefinition: TapeDefinition | null;
        tapeBuilder: ITapeBuilder;
        targetNode: TapeBuilderUI.TapeBuilderNodeAbstract;
      }
    | { isValid: false } => {
    // convert from context to the ArtifactDocument that tape-builder can work on.
    try {
      const { nodes, edges, artifactDocumentId } = graphContext.state;
      const result = tapeBuilderGraphToArtifactsDocument({
        graph: { nodes, edges },
        creditFacilities: graphContext.state.creditFacilities,
        loanProducts: graphContext.state.loanProducts,
        artifactDocumentId,
      });

      if (!result.isValid) {
        // eslint-disable-next-line no-console
        console.error('found errors in graph: ', result.errors.join(','));
        setQAStatus('Error encountered while generating tape definition');
        setQAErrors(
          result.errors.map((error) => ({
            externalMessage: error,
            properties: {},
          })),
        );
        return {
          isValid: false,
        };
      }

      // create runtime instance of tapeBuilder and run it.
      const tapeBuilder = createTapeBuilderFromArtifactsDocument(
        result.artifactsDocument,
      );

      // when executing an operation, we will be generating our dependency.
      const outGoingEdge = graphContext.state.edges.find((edge) => {
        return edge.source === nodeId;
      });

      finleyAssert(outGoingEdge, `outGoingEdge not found: ${nodeId}`);

      const targetNode = graphContext.state.nodes.find(({ metadata }) => {
        return metadata.tapeBuilderNodeId === outGoingEdge.target;
      });

      finleyAssert(targetNode, `targetNode not found: ${outGoingEdge.target}`);

      const tapeDefinition = tapeBuilder.generateTapeDefinition(
        targetNode.metadata.tapeBuilderNodeId,
      );

      if (!tapeDefinition.isValid) {
        return {
          isValid: false,
        };
      }

      return {
        isValid: true,
        tapeDefinition: tapeDefinition.data,
        tapeBuilder,
        targetNode,
      };
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('generateTapeDefinition error: ', e);
      const errors = convertErrorToStringMessages(e);
      setQAErrors(
        errors.map((error) => ({
          externalMessage: error,
          properties: {},
        })),
      );
      return {
        isValid: false,
      };
    }
  };

  const generateTape = async (
    nodeId: string,
  ): Promise<{ isValid: true; buffer: FinleyBuffer } | { isValid: false }> => {
    const node = getNodeById(nodeId);
    const nodeFriendlyName = TapeBuilderUI.getFriendlyNameForNode(node);
    try {
      await setQAStatus(`${nodeFriendlyName}: QA started`);

      const pdfExtractor: PDFExtractor = getPdfExtractor(apolloClient);
      const assetExtractor: AssetExtractor.AssetExtractor = getAssetExtractor(
        apolloClient,
        pipelineDefinitionId,
      );

      const pyXlsx2Csv2Extractor = getPyXlsx2Csv2Extractor({
        apolloClient,
        pipelineDefinitionId,
        extensionMap: latestState.current.extensions,
        getInputNodeIds,
        nodeId,
        setQAStatus,
      });

      const pyXlsxVirtualSheet2CsvExtractor = getPyXlsxVirtualSheet2CsvExtractor(
        {
          apolloClient,
          pipelineDefinitionId,
          extensionMap: latestState.current.extensions,
          getInputNodeIds,
          nodeId,
          setQAStatus,
        },
      );

      const ai2CsvExtractor = getAi2CsvExtractor({
        apolloClient,
        pipelineDefinitionId,
        nodeId,
        getInputNodeIds,
        getNodeById,
        setQAStatus,
        getNodeBuffer,
        extensionMap: latestState.current.extensions,
      });

      const pyCsvVirtualSheetExtractor = getPyCsvVirtualSheetExtractor({
        apolloClient,
        pipelineDefinitionId,
        getInputNodeIds,
        getNodeById,
        getNodeBuffer,
        nodeId,
        setQAStatus,
      });

      const output = generateTapeDefinition(nodeId);
      if (!output.isValid) {
        debugLog('generateTapeDefinition error: ', output);
        return {
          isValid: false,
        };
      }

      await setQAStatus(`${nodeFriendlyName}: Generated Tape Definition`);

      const {
        tapeBuilder,
        targetNode,
        tapeDefinition: generatedTapeDefinition,
      } = output;
      // using tapeBuilder and tapeDefinition, we can now generate the tape.
      const inputTapeBuffers = new Map<string, FinleyBuffer>();
      latestState.current.buffers.forEach((buffer, id) => {
        if (buffer !== null) {
          inputTapeBuffers.set(id, buffer);
        }
      });

      const results = await tapeBuilder.generateTape({
        artifactId: targetNode.metadata.tapeBuilderNodeId,
        inputTapeBuffers,
        pdfExtractor,
        assetExtractor,
        pyXlsx2Csv2Extractor,
        pyXlsxVirtualSheet2CsvExtractor,
        pyCsvVirtualSheetExtractor,
        ai2CsvExtractor,
        csvParser: csvConversion.smallCsvParser,
        inputBufferExtensions: latestState.current.extensions,
      });

      if (!results.isValid) {
        // eslint-disable-next-line no-console
        console.error('generateTape error: ', results);
        const errors = results.errors.map((error) => error.message);
        await setQAStatus(`${nodeFriendlyName} Error: ${errors.join(',')}`);
        await setQAErrors(results.errors.map((error) => error.structuredError));
        return {
          isValid: false,
        };
      }

      await setQAStatus(`${nodeFriendlyName}: Generated Tape`);
      setNodeBuffer(
        targetNode.metadata.tapeBuilderNodeId,
        results.data.outputBuffer,
        generatedTapeDefinition?.isJson ? 'json' : 'csv',
      );
      setNodeSupplementals(
        targetNode.metadata.tapeBuilderNodeId,
        results.supplementals,
      );
      await setQAStatus('');

      return {
        isValid: true,
        buffer: results.data.outputBuffer,
      };
    } catch (e: any) {
      // please do not delete this log - it helps debugging.
      debugLog(
        'tapeBuilder/ui/calcAgentQA/OperationPanel.tsx!generateTape: encountered an error',
        e,
      );
      const errors = convertErrorToStringMessages(e);
      await setQAStatus(`${nodeFriendlyName} Error: ${errors.join(',')}`);
      await setQAErrors(
        errors.map((error) => ({
          externalMessage: error,
          properties: {},
        })),
      );
      return {
        isValid: false,
      };
    }
  };

  // generates all tapes that can be generated.
  const generateAllTapes = async (): Promise<boolean> => {
    const operations = getAllOperationNodes(graphContext);
    let numGenerated = 0;

    // eslint-disable-next-line no-restricted-syntax
    while (operations.length > 0) {
      const executables = operations.filter((operation) => {
        const nodeId = operation.metadata.tapeBuilderNodeId;

        // notice that we pass the latest state to hasAllInputBuffersInternal
        // because the state of buffer do change during execution of
        // generateAllTapes, and we want latest.
        const hasInputs = hasAllInputBuffersInternal(
          nodeId,
          latestState.current,
        );
        const hasOutputs = hasAllOutputBuffers(nodeId);
        return hasInputs && !hasOutputs;
      });

      if (executables.length === 0) {
        setQAStatus(`Generated ${numGenerated}. No more tapes to generate`);
        return true;
      }

      // eslint-disable-next-line no-await-in-loop
      const result = await generateTape(
        executables[0].metadata.tapeBuilderNodeId,
      );

      // we wait for 100ms to give time for the UI to update.
      // between each tape generation.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!result.isValid) {
        setQAStatus(`Error encountered while generating tapes`);
        return false;
      }
      numGenerated += 1;
      operations.splice(operations.indexOf(executables[0]), 1);
    }
    return true;
  };

  const value = {
    state,

    // buffers
    hasBuffer,
    hasAllInputBuffers,
    hasAllOutputBuffers,
    getNodeBuffer,
    getNodeBufferExtension,
    setNodeBuffer,
    getNodeSupplementals,

    // QA
    generateTape,
    generateTapeDefinition,
    generateAllTapes,
  };

  return (
    <QAContext.Provider value={value}>
      <>
        {children}
        {state.errors.length > 0 && (
          <StructuredErrorsModal
            isOpen
            onClose={() => setQAErrors([])}
            errors={state.errors}
          />
        )}
      </>
    </QAContext.Provider>
  );
};
